/**
 * Version checking service to ensure users get the latest app version
 * and handle stale sessions gracefully
 */

declare const __APP_VERSION__: string;

const CURRENT_VERSION = __APP_VERSION__;
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_ENDPOINT = '/version.json';

interface VersionInfo {
  version: string;
  forceReload?: boolean;
  message?: string;
}

let versionCheckInterval: NodeJS.Timeout | null = null;
let lastKnownVersion: string | null = null;

/**
 * Check if a new version is available
 */
async function checkVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(VERSION_ENDPOINT, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      console.warn('Version check failed:', response.status);
      return null;
    }
    
    const versionInfo: VersionInfo = await response.json();
    return versionInfo;
  } catch (error) {
    console.error('Error checking version:', error);
    return null;
  }
}

/**
 * Handle version mismatch
 */
function handleVersionMismatch(versionInfo: VersionInfo) {
  const { version, forceReload, message } = versionInfo;
  
  console.log(`🔄 New version available: ${version} (current: ${CURRENT_VERSION})`);
  
  if (forceReload) {
    // Critical update - force reload immediately
    if (message) {
      alert(message);
    } else {
      alert('Una nueva versión crítica está disponible. La aplicación se recargará automáticamente.');
    }
    window.location.reload();
  } else {
    // Optional update - show notification
    const shouldReload = confirm(
      message || 
      `Hay una nueva versión disponible (${version}). ¿Deseas recargar la página para actualizarla?`
    );
    
    if (shouldReload) {
      window.location.reload();
    } else {
      // Remember to check again later
      lastKnownVersion = version;
    }
  }
}

/**
 * Start periodic version checking
 */
export function startVersionCheck() {
  // Initial check after 30 seconds
  setTimeout(() => {
    performVersionCheck();
  }, 30000);
  
  // Periodic checks
  versionCheckInterval = setInterval(() => {
    performVersionCheck();
  }, VERSION_CHECK_INTERVAL);
  
  // Check on window focus (user returns to tab)
  window.addEventListener('focus', () => {
    performVersionCheck();
  });
}

/**
 * Perform the version check
 */
async function performVersionCheck() {
  const versionInfo = await checkVersion();
  
  if (!versionInfo) return;
  
  // Skip if we've already notified about this version
  if (lastKnownVersion === versionInfo.version) return;
  
  if (versionInfo.version !== CURRENT_VERSION) {
    handleVersionMismatch(versionInfo);
  }
}

/**
 * Stop version checking
 */
export function stopVersionCheck() {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
    versionCheckInterval = null;
  }
}

/**
 * Get current app version
 */
export function getAppVersion(): string {
  return CURRENT_VERSION;
}

/**
 * Handle API errors that might indicate version mismatch
 */
export function handleAPIError(error: any): boolean {
  // Check for common errors that indicate stale client
  const errorMessage = error?.message?.toLowerCase() || '';
  
  const staleClientIndicators = [
    'collection does not exist',
    'document does not exist',
    'field does not exist',
    'permission denied',
    'invalid schema'
  ];
  
  const isStaleClient = staleClientIndicators.some(indicator => 
    errorMessage.includes(indicator)
  );
  
  if (isStaleClient) {
    console.warn('⚠️ Possible stale client detected, triggering version check');
    performVersionCheck();
    return true;
  }
  
  return false;
}

// Export version for debugging
if (typeof window !== 'undefined') {
  (window as any).__CLARA_VERSION__ = CURRENT_VERSION;
}
